import React from "react";
import { useFetch, Loading, PageHeader, StatusBadge, fmtMAD } from "@/components/common";
import { Card } from "@/components/ui/card";
import { CalendarDays, Wallet, Percent, FileText, Sparkles, Swords } from "lucide-react";

function Metric({ icon: Icon, label, value, color = "emerald" }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl bg-${color}-500/10 text-${color}-600 flex items-center justify-center`}><Icon size={20} /></div>
      <div><p className="text-xs uppercase font-bold text-muted-foreground">{label}</p><p className="text-lg font-extrabold">{value}</p></div>
    </Card>
  );
}

export default function ParentPortal() {
  const { data, loading } = useFetch("/portal/parent", []);
  if (loading) return <Loading />;
  const players = data?.players || [];

  return (
    <div>
      <PageHeader title="My Children" subtitle="Read-only overview of your player(s)" testid="parent-portal" />
      {players.length === 0 && <Card className="p-10 text-center text-muted-foreground">No linked players found for your account. Please contact the academy administration.</Card>}
      <div className="space-y-8">
        {players.map(({ player, team, payments, outstanding, documents, trainings, matches, attendance_pct, ai_analysis }) => (
          <Card key={player.id} className="p-6" data-testid={`parent-player-${player.id}`}>
            <div className="flex items-center gap-4 mb-5">
              <div className="h-16 w-16 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-2xl font-black">{player.full_name?.[0]?.toUpperCase()}</div>
              <div>
                <h2 className="text-xl font-extrabold font-heading">{player.full_name}</h2>
                <div className="flex gap-2 mt-1"><StatusBadge status={player.category} /><StatusBadge status={player.status} />{team && <span className="text-sm text-muted-foreground">{team.name}</span>}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Metric icon={Wallet} label="Outstanding" value={fmtMAD(outstanding)} color={outstanding > 0 ? "rose" : "emerald"} />
              <Metric icon={Percent} label="Attendance" value={attendance_pct != null ? `${attendance_pct}%` : "—"} />
              <Metric icon={CalendarDays} label="Trainings" value={trainings.length} color="blue" />
              <Metric icon={Swords} label="Matches" value={matches.length} color="amber" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold mb-2 flex items-center gap-2"><Wallet size={16} /> Payment History</h3>
                <div className="border border-border rounded-lg divide-y divide-border max-h-56 overflow-y-auto scrollbar-thin">
                  {payments.length === 0 && <div className="p-4 text-sm text-muted-foreground">No payments yet</div>}
                  {payments.map((p) => (
                    <div key={p.id} className="flex justify-between items-center px-4 py-2 text-sm">
                      <span>{p.month || p.type} <span className="text-muted-foreground">· {(p.date || "").slice(0, 10)}</span></span>
                      <span className="flex items-center gap-2 font-semibold">{fmtMAD(p.amount)} <StatusBadge status={p.status} /></span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-bold mb-2 flex items-center gap-2"><FileText size={16} /> Documents</h3>
                <div className="flex flex-wrap gap-2">
                  {(documents || []).length === 0 && <span className="text-sm text-muted-foreground">No documents on file</span>}
                  {(documents || []).map((d, i) => <StatusBadge key={i} status={d.status === "complete" ? "complete" : "missing"} />)}
                </div>
                <h3 className="font-bold mb-2 mt-4 flex items-center gap-2"><CalendarDays size={16} /> Upcoming Trainings</h3>
                <div className="space-y-1">
                  {trainings.slice(0, 4).map((tr) => <div key={tr.id} className="text-sm text-muted-foreground">{tr.date} {tr.time} · {tr.field || "Field"}</div>)}
                  {trainings.length === 0 && <span className="text-sm text-muted-foreground">None scheduled</span>}
                </div>
              </div>
            </div>
            {ai_analysis && (
              <div className="mt-5 p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <h3 className="font-bold mb-2 flex items-center gap-2 text-emerald-700"><Sparkles size={16} /> Performance Report</h3>
                <p className="text-sm whitespace-pre-wrap">{ai_analysis}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
